import Pica from "pica";

const MAX_OUTPUT_DIMENSION = 8192;
const MAX_PIXELS_FOR_FULL_USM = 12_000_000;

let picaInstance = null;

function getPica() {
  if (!picaInstance) {
    picaInstance = Pica({ features: ["js", "wasm", "cib"] });
  }
  return picaInstance;
}

function imageToCanvas(image) {
  const w = image.naturalWidth || image.width;
  const h = image.naturalHeight || image.height;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(image, 0, 0);
  return canvas;
}

/**
 * Logos / flex boards: few flat colors + hard edges.
 */
export function detectLogoStyle(canvas) {
  const sample = 72;
  const small = document.createElement("canvas");
  small.width = sample;
  small.height = sample;
  const ctx = small.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(canvas, 0, 0, sample, sample);
  const { data } = ctx.getImageData(0, 0, sample, sample);

  const colors = new Set();
  let edgeEnergy = 0;

  for (let y = 1; y < sample - 1; y++) {
    for (let x = 1; x < sample - 1; x++) {
      const i = (y * sample + x) * 4;
      colors.add(
        `${data[i] >> 5},${data[i + 1] >> 5},${data[i + 2] >> 5}`,
      );
      const lum =
        0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      const lumR =
        0.299 * data[i + 4] +
        0.587 * data[i + 5] +
        0.114 * data[i + 6];
      const lumD =
        0.299 * data[i + sample * 4] +
        0.587 * data[i + sample * 4 + 1] +
        0.114 * data[i + sample * 4 + 2];
      edgeEnergy += Math.abs(lum - lumR) + Math.abs(lum - lumD);
    }
  }

  return colors.size < 96 || edgeEnergy > 14000;
}

function picaUnsharpForStep(stepFactor, enhance, isLogo) {
  if (!enhance) {
    return { unsharpAmount: 0, unsharpRadius: 0, unsharpThreshold: 0 };
  }
  if (isLogo) {
    return {
      unsharpAmount: 100 + stepFactor * 25,
      unsharpRadius: 0.65,
      unsharpThreshold: 0,
    };
  }
  return {
    unsharpAmount: 70 + stepFactor * 18,
    unsharpRadius: 1.0,
    unsharpThreshold: 3,
  };
}

async function picaResizeStep(fromCanvas, targetW, targetH, unsharp) {
  const to = document.createElement("canvas");
  to.width = targetW;
  to.height = targetH;
  await getPica().resize(fromCanvas, to, {
    quality: 3,
    alpha: true,
    ...unsharp,
  });
  return to;
}

/**
 * Unsharp mask via downscaled blur (fast on large canvases).
 */
function applyUnsharpMask(canvas, amount, radiusPx, threshold = 0) {
  const w = canvas.width;
  const h = canvas.height;
  const pixels = w * h;
  if (pixels > MAX_PIXELS_FOR_FULL_USM) {
    return applyUnsharpMaskScaled(canvas, amount, radiusPx, threshold);
  }

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const original = ctx.getImageData(0, 0, w, h);

  const blurCanvas = document.createElement("canvas");
  blurCanvas.width = w;
  blurCanvas.height = h;
  const bctx = blurCanvas.getContext("2d");
  bctx.filter = `blur(${radiusPx}px)`;
  bctx.drawImage(canvas, 0, 0);
  const blurred = bctx.getImageData(0, 0, w, h);

  const data = original.data;
  const bd = blurred.data;
  for (let i = 0; i < data.length; i += 4) {
    const oR = data[i];
    const oG = data[i + 1];
    const oB = data[i + 2];
    const bR = bd[i];
    const bG = bd[i + 1];
    const bB = bd[i + 2];

    // Luma-only sharpening prevents hue shift (e.g. orange -> red)
    const oY = 0.299 * oR + 0.587 * oG + 0.114 * oB;
    const bY = 0.299 * bR + 0.587 * bG + 0.114 * bB;
    const delta = oY - bY;
    if (Math.abs(delta) >= threshold) {
      data[i] = Math.max(0, Math.min(255, Math.round(oR + amount * delta)));
      data[i + 1] = Math.max(0, Math.min(255, Math.round(oG + amount * delta)));
      data[i + 2] = Math.max(0, Math.min(255, Math.round(oB + amount * delta)));
    }
  }
  ctx.putImageData(original, 0, 0);
}

function applyUnsharpMaskScaled(canvas, amount, radiusPx, threshold) {
  const w = canvas.width;
  const h = canvas.height;
  const ratio = 0.35;
  const sw = Math.max(1, Math.round(w * ratio));
  const sh = Math.max(1, Math.round(h * ratio));

  const small = document.createElement("canvas");
  small.width = sw;
  small.height = sh;
  const sctx = small.getContext("2d", { willReadFrequently: true });
  sctx.drawImage(canvas, 0, 0, sw, sh);
  const origSmall = sctx.getImageData(0, 0, sw, sh);

  const blurSmall = document.createElement("canvas");
  blurSmall.width = sw;
  blurSmall.height = sh;
  const bctx = blurSmall.getContext("2d");
  bctx.filter = `blur(${Math.max(1, radiusPx * ratio)}px)`;
  bctx.drawImage(small, 0, 0);
  const blurData = bctx.getImageData(0, 0, sw, sh);

  const data = origSmall.data;
  const bd = blurData.data;
  for (let i = 0; i < data.length; i += 4) {
    const oR = data[i];
    const oG = data[i + 1];
    const oB = data[i + 2];
    const bR = bd[i];
    const bG = bd[i + 1];
    const bB = bd[i + 2];

    const oY = 0.299 * oR + 0.587 * oG + 0.114 * oB;
    const bY = 0.299 * bR + 0.587 * bG + 0.114 * bB;
    const delta = oY - bY;
    if (Math.abs(delta) >= threshold) {
      data[i] = Math.max(0, Math.min(255, Math.round(oR + amount * delta)));
      data[i + 1] = Math.max(0, Math.min(255, Math.round(oG + amount * delta)));
      data[i + 2] = Math.max(0, Math.min(255, Math.round(oB + amount * delta)));
    }
  }
  sctx.putImageData(origSmall, 0, 0);

  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(small, 0, 0, w, h);
}

function applyEdgeAwareDenoise(canvas, strength = 0.5) {
  const w = canvas.width;
  const h = canvas.height;
  const pixels = w * h;
  if (pixels > 6_000_000) {
    return applyEdgeAwareDenoiseScaled(canvas, strength);
  }

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const imageData = ctx.getImageData(0, 0, w, h);
  const src = imageData.data;
  const out = new Uint8ClampedArray(src);

  const lumaThreshold = 8 + strength * 24;
  const blend = 0.2 + strength * 0.45;

  const luma = (r, g, b) => 0.299 * r + 0.587 * g + 0.114 * b;

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = (y * w + x) * 4;
      const cR = src[i];
      const cG = src[i + 1];
      const cB = src[i + 2];
      const cY = luma(cR, cG, cB);

      let sumR = 0;
      let sumG = 0;
      let sumB = 0;
      let weight = 0;

      for (let oy = -1; oy <= 1; oy++) {
        for (let ox = -1; ox <= 1; ox++) {
          const ni = ((y + oy) * w + (x + ox)) * 4;
          const nR = src[ni];
          const nG = src[ni + 1];
          const nB = src[ni + 2];
          const nY = luma(nR, nG, nB);
          const dY = Math.abs(nY - cY);
          if (dY <= lumaThreshold) {
            const spatial = ox === 0 && oy === 0 ? 2 : 1;
            sumR += nR * spatial;
            sumG += nG * spatial;
            sumB += nB * spatial;
            weight += spatial;
          }
        }
      }

      if (weight > 0) {
        const aR = sumR / weight;
        const aG = sumG / weight;
        const aB = sumB / weight;
        out[i] = Math.round(cR * (1 - blend) + aR * blend);
        out[i + 1] = Math.round(cG * (1 - blend) + aG * blend);
        out[i + 2] = Math.round(cB * (1 - blend) + aB * blend);
      }
    }
  }

  imageData.data.set(out);
  ctx.putImageData(imageData, 0, 0);
}

function applyEdgeAwareDenoiseScaled(canvas, strength) {
  const w = canvas.width;
  const h = canvas.height;
  const ratio = Math.sqrt(6_000_000 / (w * h));
  const sw = Math.max(1, Math.round(w * ratio));
  const sh = Math.max(1, Math.round(h * ratio));

  const small = document.createElement("canvas");
  small.width = sw;
  small.height = sh;
  const sctx = small.getContext("2d");
  sctx.imageSmoothingEnabled = true;
  sctx.imageSmoothingQuality = "high";
  sctx.drawImage(canvas, 0, 0, sw, sh);
  applyEdgeAwareDenoise(small, strength);

  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(small, 0, 0, w, h);
}

function applyLogoCrispPass(canvas) {
  const w = canvas.width;
  const h = canvas.height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const imageData = ctx.getImageData(0, 0, w, h);
  const { data } = imageData;
  const contrast = 1.04;

  for (let i = 0; i < data.length; i += 4) {
    // Micro contrast only on luminance; keep chroma ratios to preserve brand colors.
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const y = 0.299 * r + 0.587 * g + 0.114 * b;
    const y2 = (y - 128) * contrast + 128;
    const delta = y2 - y;
    data[i] = Math.max(0, Math.min(255, Math.round(r + delta)));
    data[i + 1] = Math.max(0, Math.min(255, Math.round(g + delta)));
    data[i + 2] = Math.max(0, Math.min(255, Math.round(b + delta)));
  }
  ctx.putImageData(imageData, 0, 0);
}

export function getEffectiveScale(width, height, requestedScale) {
  if (!width || !height || requestedScale <= 1) return 1;
  const maxDim = Math.max(width, height);
  const cap = Math.floor(MAX_OUTPUT_DIMENSION / maxDim);
  return Math.min(requestedScale, Math.max(1, cap));
}

export function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

/**
 * High-quality upscale (Lanczos via Pica + unsharp).
 * @param {'auto'|'logo'|'photo'} mode
 */
export async function upscaleImageAsync(
  image,
  scale,
  enhance = true,
  mode = "auto",
  options = {},
) {
  const { strongDenoise = false } = options;
  const srcW = image.naturalWidth || image.width;
  const srcH = image.naturalHeight || image.height;
  const effectiveScale = getEffectiveScale(srcW, srcH, scale);

  let current = imageToCanvas(image);
  let w = srcW;
  let h = srcH;

  const isLogo =
    mode === "logo" || (mode === "auto" && detectLogoStyle(current));

  if (effectiveScale <= 1) {
    if (enhance) {
      if (!isLogo) {
        applyEdgeAwareDenoise(current, strongDenoise ? 0.8 : 0.55);
      }
      applyUnsharpMask(current, isLogo ? 1.4 : 0.9, isLogo ? 1.5 : 2, 0);
      if (isLogo) applyLogoCrispPass(current);
    }
    return {
      canvas: current,
      effectiveScale: 1,
      width: w,
      height: h,
      isLogo,
    };
  }

  let remaining = effectiveScale;
  let stepIndex = 0;

  if (enhance && !isLogo) {
    // Pre-clean noise so upscaling doesn't magnify grain.
    applyEdgeAwareDenoise(current, strongDenoise ? 0.82 : 0.5);
  }

  while (remaining > 1) {
    const step = remaining >= 2 ? 2 : remaining;
    const newW = Math.round(w * step);
    const newH = Math.round(h * step);
    const unsharp = picaUnsharpForStep(stepIndex + 1, enhance, isLogo);
    current = await picaResizeStep(current, newW, newH, unsharp);
    w = newW;
    h = newH;
    remaining /= step;
    stepIndex += 1;
  }

  if (enhance) {
    if (!isLogo) {
      applyEdgeAwareDenoise(current, strongDenoise ? 0.65 : 0.35);
    }
    applyUnsharpMask(
      current,
      isLogo ? 1.65 : strongDenoise ? 1.05 : 1.25,
      isLogo ? 1.2 : strongDenoise ? 1.35 : 1.8,
      isLogo ? 0 : strongDenoise ? 0 : 1,
    );
    if (isLogo) {
      applyUnsharpMask(current, 0.85, 0.6, 0);
      applyLogoCrispPass(current);
    }
  }

  return {
    canvas: current,
    effectiveScale,
    width: w,
    height: h,
    isLogo,
  };
}

export function canvasToBlob(canvas, type = "image/png", quality = 0.95) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Export failed"));
      },
      type,
      quality,
    );
  });
}

export function getRecommendedExportType(sourceType, isLogo, enhance) {
  if (isLogo || enhance) return "image/png";
  if (sourceType === "image/jpeg") return "image/jpeg";
  if (sourceType === "image/webp") return "image/webp";
  return "image/png";
}
